import { Link } from 'react-router-dom';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Shift = { icon: SvgIconComponent; title: string; body: string; linkTo?: string };

const shifts: Shift[] = [
  {
    icon: GroupsOutlinedIcon,
    title: 'Tribal knowledge stops scaling around six or seven people',
    body: 'What worked when everyone sat in on every client conversation breaks once there are enough techs that no single person has full context on every account. The system of record has to carry what used to live in group chat.',
    linkTo: '/blog/when-to-hire-your-first-tech',
  },
  {
    icon: SupervisorAccountOutlinedIcon,
    title: 'Role-based access stops being optional',
    body: 'A shared login or "everyone has admin" gets away with it at three people. At ten, it\'s an audit finding waiting to happen — and the client asking who on your team can see their data deserves a real answer.',
  },
  {
    icon: ScheduleOutlinedIcon,
    title: 'Coverage windows become a real scheduling problem',
    body: 'Someone has to actually be on call, and dispatch has to route a ticket to whoever\'s covering, not to a single owner who used to just handle everything themselves.',
  },
  {
    icon: AccountTreeOutlinedIcon,
    title: 'You need a rollup, not just a queue',
    body: 'A dispatcher triaging a busy morning needs cross-client visibility — tickets, alerts, and devices across every account in one view — without weakening the isolation boundary between clients underneath it.',
    linkTo: '/platform/tenant-management',
  },
];

export function SolutionsGrowingMsps(): JSX.Element {
  useSeo({
    title: 'For growing MSPs',
    description:
      'What actually changes operationally once an MSP grows past a founder-and-one-tech shop — role-based access, coverage windows, and a real dispatch view instead of tribal knowledge.',
    path: '/solutions/growing-msps',
  });

  return (
    <div>
      <PageHero
        eyebrow="For growing MSPs"
        title="What breaks between 'a few people' and 'a real team.'"
        subtitle="The problems a 1-5 person MSP has are real, but they're different from the ones a growing team hits next. This is the companion page for what changes once you're past the first hire."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {shifts.map((s, i) => {
              const Icon = s.icon;
              const card = (
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{s.body}</p>
                </div>
              );
              return (
                <Reveal key={s.title} delay={i * 90}>
                  {s.linkTo ? (
                    <Link to={s.linkTo} className="block no-underline">
                      {card}
                    </Link>
                  ) : (
                    card
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              Still the same architecture, not a tier upgrade
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Nothing about tenant isolation, signed remote execution, or encrypted secrets changes
              as you add headcount — you were never on a lightweight version of any of it. What
              actually changes is which capabilities you start leaning on: role-based access, HQ
              cross-tenant boards, and dispatch become load-bearing instead of nice-to-have. See{' '}
              <Link to="/for-small-msps" className="text-nexus-purple-light no-underline hover:underline">
                the small-MSP page
              </Link>{' '}
              for where the journey usually starts.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Growing past a founder-and-one-tech shop?
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort — we&apos;ll talk through what your team actually
                needs at your current size.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
